/**
 * Decline reasons — the DOMAIN vocabulary (s179, TWO-REASON-DECLINE-1, D-178.15).
 *
 * Separate from `decline-copy.ts` on purpose: this module is what a reason IS, that module is
 * what a reason SAYS. The route validates against this; the copy module renders from it. A
 * surface that needs the set imports it here rather than retyping three string literals.
 *
 * 🔴 WHY NOT `declineReasonEnum.enumValues` FROM THE SCHEMA. That would be the derived form, and
 * it is the right instinct — but `shared/schema.ts` is a drizzle module, so a VALUE import of it
 * pulls `drizzle-orm/pg-core` into the client bundle (the client's existing schema imports are
 * type-only and erase at build). So the list is declared here and a parity test asserts it equals
 * `declineReasonEnum.enumValues` — derived-not-duplicated enforced by a guard rather than by an
 * import (D-178.2). Adding a member to one without the other fails that test by name.
 *
 * The TWO, and why one boolean could not carry them (the whole charter):
 *   • dont_have  — the fact does not exist. The executor stops looking.
 *   • wont_store — it exists; the planner chose not to type it here. The executor still has to get
 *                  it, and `decline_note` may tell them where to look first.
 *
 * 🔴 WHY TWO AND NOT THREE (Reade's ruling, s179 rebuild). The first cut carried a third member,
 * `giving_another_way`. That was a real distinction in the EXECUTOR'S world and not one in the
 * PLANNER'S: "I'd rather not store this here" and "I'm providing this another way" are one thought
 * — *I am not typing this in here*. What happens instead is a FOLLOW-UP QUESTION ("Are you
 * providing this another way? Where will your executor find it?"), whose answer is free text, not
 * an enum member. Modelling it as a third reason forced the planner to pre-classify their own
 * situation before we had asked them anything, and produced two options they read as synonyms.
 *
 * So the note carries what the third member was reaching for, and carries it BETTER: "in the
 * envelope in my safe deposit box" is a far more useful executor instruction than any sentence we
 * could generate from a category.
 *
 * `wont_store` is the WITHHELD class: it resolves the completion bar (the planner has consciously
 * answered) while staying counted and named as executor work. 🔴 A note does NOT change that — the
 * executor still has work; the note only says where to look first. `dont_have` simply closes.
 */

/** The two decline reasons, in the order surfaces should offer them. */
export const DECLINE_REASONS = ["dont_have", "wont_store"] as const;

export type DeclineReason = (typeof DECLINE_REASONS)[number];

/**
 * The WITHHELD subset — a reason whose field still owes the executor something.
 *
 * 🔴 This set, not a bare `=== "wont_store"`, is what the lens and every surface ask. A one-member
 * set looks like ceremony today; it is what stops the next reason being added by scattering
 * comparisons, and it is why dropping the third member was a one-line change here rather than a
 * hunt through the consumers.
 */
export const WITHHELD_DECLINE_REASONS: readonly DeclineReason[] = ["wont_store"];

/** Runtime validator — the route's 400 gate and the only place an unknown string is rejected. */
export function isDeclineReason(v: unknown): v is DeclineReason {
  return typeof v === "string" && (DECLINE_REASONS as readonly string[]).includes(v);
}

/**
 * Does this reason leave the executor with work to do?
 *
 * A NULL/absent reason is NOT withheld: it carries no claim about the fact's existence, so
 * naming it as executor work would be inventing information we do not have.
 */
export function isWithheldReason(v: unknown): v is DeclineReason {
  return isDeclineReason(v) && WITHHELD_DECLINE_REASONS.includes(v);
}

/**
 * Max length of `decline_note` — the planner's "where will your executor find it?" text.
 *
 * 🔴 A LOCATION, NOT A NARRATIVE. The cap is deliberately tight: it is long enough for "in the
 * envelope in my safe deposit box at the Queen St branch" and short enough that the field does not
 * invite a paragraph — and a field that does not invite a paragraph is less likely to be used as a
 * place to paste the secret itself, which is the one thing it must never hold. The interface
 * carries most of that weight (placeholder + helper line); this is the backstop.
 */
export const DECLINE_NOTE_MAX_LENGTH = 200;

/** Normalise a note for storage: trimmed, capped, and empty-to-null. */
export function normalizeDeclineNote(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const trimmed = v.trim();
  return trimmed === "" ? null : trimmed.slice(0, DECLINE_NOTE_MAX_LENGTH);
}
